import React, { useRef, useCallback, useMemo, useEffect } from 'react';

const DATA_ATTR = 'data-focus-method';

export const useControlFocusType = () => {
	const focusMethod = useRef<string>('');
	const previousFocusMethod = useRef<string>('');

	const EVENT_TYPE = useMemo(() => {
		return {
			key: 'key',
			mouse: 'mouse',
		};
	}, []);

	useEffect(() => {
		focusMethod.current = EVENT_TYPE.key;
	}, [EVENT_TYPE.key]);

	const isKeyboardAction = useCallback((e: React.MouseEvent): boolean => {
		return !(e.clientX > 0 || e.clientY > 0);
	}, []);

	const setFocusMethodOnElement = useCallback((element: Element | null, focusMethod: string): void => {
		element && element.setAttribute(DATA_ATTR, focusMethod);
	}, []);

	const handleElementFocus = useCallback(
		(e: React.FocusEvent): void => {
			const element = e.target as HTMLElement;
			const currentFocusMethod = focusMethod.current === '' ? 'key' : focusMethod.current;

			setFocusMethodOnElement(element, currentFocusMethod);
		},
		[setFocusMethodOnElement],
	);

	const handleElementBlur = useCallback((e: React.FocusEvent): void => {
		e.target.removeAttribute(DATA_ATTR);

		focusMethod.current = '';
		previousFocusMethod.current = '';
	}, []);

	const handleElementClick = useCallback(
		(e: React.MouseEvent): void => {
			e.persist();

			if (isKeyboardAction(e)) {
				setFocusMethodOnElement(e.currentTarget, EVENT_TYPE.key);
			} else {
				setFocusMethodOnElement(e.currentTarget, EVENT_TYPE.mouse);
			}
		},
		[EVENT_TYPE, isKeyboardAction, setFocusMethodOnElement],
	);

	return {
		handleElementBlur,
		handleElementFocus,
		handleElementClick,
	};
};
